import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { 
  DEFAULT_PAGE_SIZE, 
  SEARCH_DEBOUNCE_DELAY,
  DATE_RANGE_OPTIONS,
  getJobCardDateRange,
  JOB_TYPE_OPTIONS
} from './config';
import { useServerFiltering } from '../../utils/useServerFiltering';
import { useUrlFilters, JOB_CARDS_FILTER_DEFINITIONS } from '../../utils/useUrlFilters';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import SearchableClientDropdown from '../../components/SearchableClientDropdown';
import { ColumnDef } from '@tanstack/react-table';
import Dropdown from '../../components/Dropdown';
import { DropdownItem } from '../../components/Dropdown/config';
import JobCardModal from './components/JobCardModal';
import { useRestoredJobCardModal } from './components/JobCardModal/restore';
import TableSkeleton from '../../components/TableSkeleton';
import TextInput from '../../components/TextInput';
import ListPage, { FilterTrigger, SearchInput, StatusSegments, useNarrowViewport } from '../../components/ListPage';

import { JobCard } from './config';

interface TeamMember {
  id: string;
  first_name: string;
  last_name: string;
  type: string;
}

interface WorkflowStatus {
  id: number;
  status_name: string;
  job_types: string[];
}

const JobCards: React.FC = () => {
  // URL-synced filter state - persists across navigation
  const { 
    filters: urlFilters, 
    setFilter,
    setFilters: setUrlFilters,
    resetFilters: resetUrlFilters,
    hasActiveFilters
  } = useUrlFilters(JOB_CARDS_FILTER_DEFINITIONS);

  // Derive filter values from URL state
  const searchInput = urlFilters.search;
  const selectedDateRange = urlFilters.dateRange;
  const selectedProjectLead = urlFilters.projectLead;
  const selectedStatus = urlFilters.status;
  const selectedTypes = urlFilters.types;
  const selectedClient = useMemo(() => 
    urlFilters.client ? [urlFilters.client] : [], 
    [urlFilters.client]
  );
  const selectedProjectStatus = urlFilters.projectStatus;
  const customStartDate = urlFilters.fromDate;
  const customEndDate = urlFilters.toDate;
  const urlPage = urlFilters.page;
  const urlPageSize = urlFilters.pageSize;
  
  // UI state
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJobCard, setSelectedJobCard] = useState<JobCard | null>(null);

  // Hooks
  const session = useSession();
  const supabase = useSupabaseClient();

  // Server-side filtering hook
  const {
    data: jobCards,
    pagination,
    summary,
    loading,
    filtering,
    error,
    setFilters
  } = useServerFiltering<JobCard>({
    rpcFunction: 'get_filtered_job_cards',
    defaultFilters: {
      search_term: null,
      project_lead_filter: null,
      date_range_filter: 'allTime',
      custom_start_date: null,
      custom_end_date: null,
      status_filter: null,
      types_filter: null,
      client_filter: null,
      project_status_filter: null,
      page_size: DEFAULT_PAGE_SIZE,
      page_offset: 0,
      sort_by: 'job_number',
      sort_order: 'desc'
    },
    debounceDelay: SEARCH_DEBOUNCE_DELAY
  });

  // Sync URL filter state with server filtering
  useEffect(() => {
    const pageSize = urlPageSize || DEFAULT_PAGE_SIZE;
    const pageOffset = ((urlPage || 1) - 1) * pageSize;
    
    let startDate = null;
    let endDate = null;
    
    if (selectedDateRange === 'custom') {
      startDate = customStartDate || null;
      endDate = customEndDate || null;
    } else if (selectedDateRange !== 'allTime') {
      const dateRange = getJobCardDateRange(selectedDateRange);
      startDate = dateRange.from || null;
      endDate = dateRange.to || null;
    }
    
    setFilters({
      search_term: searchInput || null,
      project_lead_filter: selectedProjectLead.length > 0 ? selectedProjectLead.map(id => {
        const member = teamMembers.find(tm => tm.id === id);
        return member ? `${member.first_name} ${member.last_name}` : id;
      }) : null,
      date_range_filter: selectedDateRange || 'allTime',
      custom_start_date: startDate,
      custom_end_date: endDate,
      status_filter: selectedStatus.length > 0 ? selectedStatus : null,
      types_filter: selectedTypes.length > 0 ? selectedTypes : null,
      client_filter: selectedClient.length > 0 ? selectedClient : null,
      project_status_filter: selectedProjectStatus.length > 0 ? selectedProjectStatus : null,
      page_size: pageSize,
      page_offset: pageOffset
    });
  }, [searchInput, selectedProjectLead, selectedDateRange, selectedStatus, selectedTypes, selectedClient, selectedProjectStatus, customStartDate, customEndDate, teamMembers, urlPage, urlPageSize, setFilters]);

  // Clear invalid project status selections when types filter changes
  useEffect(() => {
    if (selectedTypes.length > 0 && selectedProjectStatus.length > 0) {
      // Get valid statuses for selected types
      const validStatuses = workflowStatuses
        .filter(status => status.job_types.some(jobType => selectedTypes.includes(jobType)))
        .map(status => status.status_name);
      
      // Remove any selected statuses that are no longer valid
      const stillValidStatuses = selectedProjectStatus.filter(s => validStatuses.includes(s));
      if (stillValidStatuses.length !== selectedProjectStatus.length) {
        setFilter('projectStatus', stillValidStatuses);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypes, workflowStatuses]); // Don't include selectedProjectStatus to avoid infinite loop

  // Handler functions
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlFilters({ search: e.target.value, page: 1 });
  }, [setUrlFilters]);

  const handleDateRangeChange = useCallback((values: string | string[]) => {
    const newValue = Array.isArray(values) ? values[0] : values;
    if (newValue === 'custom') {
      setUrlFilters({ dateRange: 'custom', page: 1 });
    } else {
      setUrlFilters({ dateRange: newValue || 'allTime', fromDate: '', toDate: '', page: 1 });
    }
  }, [setUrlFilters]);

  const handleCustomStartDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter('fromDate', e.target.value);
  }, [setFilter]);

  const handleCustomEndDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter('toDate', e.target.value);
  }, [setFilter]);

  const handleProjectLeadChange = useCallback((leads: string[]) => {
    setUrlFilters({ projectLead: leads, page: 1 });
  }, [setUrlFilters]);

  const handleStatusChange = useCallback((status: string[]) => {
    setUrlFilters({ status, page: 1 });
  }, [setUrlFilters]);

  const handleTypesChange = useCallback((types: string[]) => {
    setUrlFilters({ types, page: 1 });
  }, [setUrlFilters]);

  const handleClientChange = useCallback((clientId: string | number | null) => {
    if (clientId === null || clientId === 'none') {
      setUrlFilters({ client: '', page: 1 });
    } else {
      setUrlFilters({ client: clientId.toString(), page: 1 });
    }
  }, [setUrlFilters]);

  const handleProjectStatusChange = useCallback((statuses: string[]) => {
    setUrlFilters({ projectStatus: statuses, page: 1 });
  }, [setUrlFilters]);

  const handleClearFilters = useCallback(() => {
    resetUrlFilters();
  }, [resetUrlFilters]);

  const handleClearSearch = useCallback(() => {
    setUrlFilters({ search: '', page: 1 });
  }, [setUrlFilters]);

  const isNarrow = useNarrowViewport();

  const money = (n: number) => `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // Under a date range each figure carries its period movement as a chip,
  // so the three columns take more room; the Project column gives it up.
  const ranged = selectedDateRange !== 'allTime';

  /** "Nicos Terpkos" -> "NT"; a single word gives its first two letters. */
  const initials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0]?.slice(0, 2).toUpperCase() ?? '';
  };

  // Fetch active team members and workflow statuses for filtering
  const fetchStaticData = useCallback(async () => {
    try {
      setTeamMembersLoading(true);
      
      // Fetch team members
      const { data: teamData, error: teamError } = await supabase
        .from('active_team_members')
        .select('id, first_name, last_name, type')
        .order('first_name');

      if (teamError) throw teamError;
      setTeamMembers(teamData || []);

      // Fetch all workflow statuses with their job types
      const { data: statusData, error: statusError } = await supabase
        .rpc('get_all_workflow_statuses');

      if (statusError) {
        console.error('Error fetching workflow statuses:', statusError);
      } else {
        setWorkflowStatuses(statusData || []);
      }
    } catch (err) {
      console.error('Error fetching static data:', err);
      // Don't set error state here as it would interfere with server filtering error
    } finally {
      setTeamMembersLoading(false);
    }
  }, [supabase]);

  // Fetch static data on mount
  useEffect(() => {
    if (session) {
      fetchStaticData();
    }
  }, [session, fetchStaticData]);

  // Handle row click to open modal (similar to Project Register)
  // Back from the invoice builder: reopen the modal it was opened from, on
  // the same tab. A restored modal is given an explicit entity so it hydrates
  // itself, rather than waiting on this page's (paged) list to find the row.
  const { restoredProps, clearRestored } = useRestoredJobCardModal();

  const handleRowClick = React.useCallback((jobCard: JobCard) => {
    clearRestored();
    setSelectedJobCard(jobCard);
    setIsModalOpen(true);
  }, [clearRestored]);

  // Create table columns with flexible width configuration
  const columns = React.useMemo<ColumnDef<JobCard>[]>(() => [
    {
      accessorKey: 'job_number',
      header: 'Job #',
      meta: { minWidth: '96px', width: '96px' },
    },
    {
      accessorKey: 'project_name',
      header: 'Project',
      meta: { minWidth: '200px', width: 'auto' },
      cell: ({ row }) => <span title={row.original.project_name}>{row.original.project_name}</span>,
    },
    {
      accessorKey: 'client_name',
      header: 'Client',
      meta: { minWidth: '150px', width: '18%' },
      cell: ({ row }) => <span title={row.original.client_name ?? undefined}>{row.original.client_name ?? '—'}</span>,
    },
    {
      accessorKey: 'project_lead_name',
      header: 'Lead',
      meta: { minWidth: '96px', width: '96px' },
      cell: ({ row }) => {
        const name = row.original.project_lead_name?.trim();
        if (!name) return '—';
        return <span className="ui-avatar" title={name} aria-label={name}>{initials(name)}</span>;
      },
    },
    {
      accessorKey: 'total_hours',
      header: 'Hours',
      meta: { minWidth: '120px', width: ranged ? '164px' : '120px', align: 'right' },
      cell: ({ row }) => {
        const jobCard = row.original;
        const totalHours = jobCard.total_hours;
        const filteredHours = jobCard.filtered_hours || 0;
        
        return (
          <div className="table-cell-with-change">
            <span className="total-value">{totalHours.toFixed(2)}</span>
            {filteredHours > 0 && selectedDateRange !== 'allTime' && (
              <span className="filtered-change">+{filteredHours.toFixed(2)}</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'total_cost',
      header: 'Expensed',
      meta: { minWidth: '136px', width: ranged ? '212px' : '136px', align: 'right' },
      cell: ({ row }) => {
        const jobCard = row.original;
        const totalCost = jobCard.total_cost;
        const filteredCost = jobCard.filtered_cost || 0;
        
        return (
          <div className="table-cell-with-change">
            <span className="total-value">{money(totalCost)}</span>
            {filteredCost > 0 && selectedDateRange !== 'allTime' && (
              <span className="filtered-change">+{money(filteredCost)}</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'total_invoiced',
      header: 'Invoiced',
      meta: { minWidth: '136px', width: ranged ? '212px' : '136px', align: 'right' },
      cell: ({ row }) => {
        const jobCard = row.original;
        const totalInvoiced = jobCard.total_invoiced;
        const filteredInvoiced = jobCard.filtered_invoiced || 0;
        
        return (
          <div className="table-cell-with-change">
            <span className="total-value">{money(totalInvoiced)}</span>
            {filteredInvoiced > 0 && selectedDateRange !== 'allTime' && (
              <span className="filtered-change">+{money(filteredInvoiced)}</span>
            )}
          </div>
        );
      },
    },
  ], [selectedDateRange, ranged]);

  // Create project lead dropdown items (Engineers only)
  const projectLeadItems: DropdownItem[] = [
    { id: 'all', label: 'All Project Leads', value: 'all' },
    ...teamMembers
      .filter(member => member.type === 'Engineer')
      .map(member => ({
        id: member.id,
        label: `${member.first_name} ${member.last_name}`,
        value: member.id
      }))
  ];

  // Create date range dropdown items
  const dateRangeItems: DropdownItem[] = DATE_RANGE_OPTIONS.map((option) => ({
    id: option.value,
    label: option.label,
    value: option.value
  }));

  // Create job type dropdown items
  const jobTypeItems: DropdownItem[] = [
    { id: 'all', label: 'All Types', value: 'all' },
    ...JOB_TYPE_OPTIONS.map(type => ({
      id: type,
      label: type,
      value: type
    }))
  ];

  // Create project status dropdown items - dynamic based on selected job types
  const projectStatusItems: DropdownItem[] = useMemo(() => {
    // Filter statuses based on selected job types
    let filteredStatuses = workflowStatuses;
    
    if (selectedTypes.length > 0) {
      // Show only statuses that belong to at least one selected job type
      filteredStatuses = workflowStatuses.filter(status =>
        status.job_types.some(jobType => selectedTypes.includes(jobType))
      );
    }
    
    return filteredStatuses.map(status => ({
      id: status.status_name,
      label: status.status_name,
      value: status.status_name
    }));
  }, [workflowStatuses, selectedTypes]);

  // Handle pagination - update URL
  const handlePageChange = useCallback((page: number) => {
    setFilter('page', page);
  }, [setFilter]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setUrlFilters({ pageSize: newPageSize, page: 1 });
  }, [setUrlFilters]);

  // Current page from pagination (for display)
  const currentPage = pagination?.page || urlPage || 1;
  const currentPageSize = pagination?.page_size || urlPageSize || DEFAULT_PAGE_SIZE;

  // Handle error state
  if (error) return <div className="error-container">Error: {error}</div>;

  return (
    <ListPage
      title="Job Cards"
      className="job-cards-page"
      loading={teamMembersLoading}
      skeleton={{ segmentCount: 0, filterCount: 5, showAction: false }}
      search={
        <SearchInput
          placeholder="Search by project name or job number…"
          value={searchInput}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
          aria-label="Search job cards"
        />
      }
      segments={
        selectedDateRange !== 'allTime' && summary ? (
          <StatusSegments
            aria-label="Invoiced in this period"
            size={isNarrow ? 'sm' : 'md'}
            value={selectedStatus.includes('Invoiced') ? 'Invoiced' : selectedStatus.includes('Not Invoiced') ? 'Not Invoiced' : 'all'}
            onChange={(v) => handleStatusChange(v === 'all' ? [] : [v])}
            segments={[
              { value: 'all', label: 'All', count: (summary.not_invoiced_count || 0) + (summary.invoiced_count || 0) },
              { value: 'Not Invoiced', label: 'Not Invoiced', count: summary.not_invoiced_count || 0 },
              { value: 'Invoiced', label: 'Invoiced', count: summary.invoiced_count || 0 },
            ]}
          />
        ) : undefined
      }
      filters={
        <>
          <FilterTrigger label="Type" active={selectedTypes.length > 0}>
            <Dropdown items={jobTypeItems} value={selectedTypes} onChange={handleTypesChange} placeholder="All" multiSelect />
          </FilterTrigger>
          <FilterTrigger label="Status" active={selectedProjectStatus.length > 0}>
            <Dropdown items={projectStatusItems} value={selectedProjectStatus} onChange={handleProjectStatusChange} placeholder="All" multiSelect searchable />
          </FilterTrigger>
          <FilterTrigger label="Client" active={selectedClient.length > 0}>
            <SearchableClientDropdown
              value={selectedClient.length > 0 ? selectedClient[0] : null}
              onChange={(clientId: string | number | null) => handleClientChange(clientId)}
              placeholder="All"
              allowNone={true}
              noneLabel="All Clients"
            />
          </FilterTrigger>
          <FilterTrigger label="Lead" active={selectedProjectLead.length > 0}>
            <Dropdown items={projectLeadItems} value={selectedProjectLead} onChange={handleProjectLeadChange} placeholder="All" searchable multiSelect />
          </FilterTrigger>
          <FilterTrigger label="Date" active={selectedDateRange !== 'allTime'}>
            <Dropdown items={dateRangeItems} value={selectedDateRange} onChange={handleDateRangeChange} placeholder="All time" />
          </FilterTrigger>
          {selectedDateRange === 'custom' && (
            <>
              <FilterTrigger label="From" active>
                <TextInput size="sm" type="date" value={customStartDate} onChange={handleCustomStartDateChange} aria-label="Start date" />
              </FilterTrigger>
              <FilterTrigger label="To" active>
                <TextInput size="sm" type="date" value={customEndDate} onChange={handleCustomEndDateChange} aria-label="End date" />
              </FilterTrigger>
            </>
          )}
        </>
      }
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
      resultsText={
        !loading && pagination && (
          <>
            Showing {pagination.total_records > 0 ? ((currentPage - 1) * currentPageSize) + 1 : 0}–{Math.min(currentPage * currentPageSize, pagination.total_records)} of {pagination.total_records.toLocaleString()} job cards
          </>
        )
      }
      footer={
        <Pagination
          variant="bar"
          currentPage={currentPage}
          totalPages={pagination?.total_pages ?? 1}
          totalRecords={pagination?.total_records ?? 0}
          pageSize={currentPageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          hasNext={pagination?.has_next ?? false}
          hasPrevious={pagination?.has_previous ?? false}
          loading={loading || filtering}
          showPageInfo
          pageSizeOptions={[25, 50, 100]}
        />
      }
    >
      {teamMembersLoading || loading ? (
        <TableSkeleton compact rows={14} columns={7} showHeader />
      ) : jobCards.length === 0 ? (
        <div className="list-page__empty">
          {hasActiveFilters ? 'No job cards match these filters.' : 'No job cards yet.'}
        </div>
      ) : (
        <div className={`list-page__body ${filtering ? 'is-filtering' : ''}`.trim()}>
          <Table data={jobCards} columns={columns} onRowClick={handleRowClick} stickyHeader />
        </div>
      )}

      <JobCardModal
        onClose={() => {
          setIsModalOpen(false);
          clearRestored();
        }}
        {...(restoredProps ?? { isOpen: isModalOpen, jobCard: selectedJobCard })}
      />
    </ListPage>
  );
};

export default JobCards;
